import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Search,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "received", label: "Received" },
  { value: "pending", label: "Pending" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];

function safeDate(value) {
  if (!value) return "—";
  return String(value).slice(0, 10);
}

function csvEscape(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return `"${s.replace(/"/g, '""')}"`;
}

function badgeClass(status) {
  if (status === "Received") {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  return "bg-amber-50 text-amber-700 border-amber-200";
}

export default function PaymentsReceivedPendingDetail() {
  const nav = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const fromDate = params.get("from") || "";
  const toDate = params.get("to") || "";
  const initialStatus = params.get("status") || "all";

  const [status, setStatus] = useState(initialStatus);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  const [sort, setSort] = useState({
    field: "CInvDate",
    direction: "desc",
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await api.post(
          "/ai/ceo-chat/payments-received-pending/detail",
          {
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
            status,
          },
          { timeout: 120000 }
        );

        const data = response?.data ?? response;

        if (!cancelled) {
          setRows(Array.isArray(data?.rows) ? data.rows : []);
          setSummary(data?.summary || null);
          setMeta(data || null);
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setSummary(null);
          setMeta(null);
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "Payments Received / Pending details could not be loaded safely."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate, status]);

  useEffect(() => {
    setPage(1);
  }, [search, status, pageSize, sort]);

  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "").toLowerCase().includes(q)
      )
    );
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const copy = [...searchedRows];
    if (!sort.field) return copy;

    copy.sort((a, b) => {
      const result = String(a?.[sort.field] ?? "").localeCompare(
        String(b?.[sort.field] ?? ""),
        undefined,
        { numeric: true }
      );
      return sort.direction === "desc" ? -result : result;
    });

    return copy;
  }, [searchedRows, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pagedRows = sortedRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const changeSort = (field) => {
    setSort((current) => ({
      field,
      direction:
        current.field === field && current.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const applyStatus = (value) => {
    setStatus(value);

    const next = new URLSearchParams(location.search);
    next.set("scope", "payments");
    next.set("status", value);

    nav(
      `/ceo-command-center/payments-received-pending?${next.toString()}`,
      { replace: true }
    );
  };

  const exportCsv = () => {
    if (!sortedRows.length) return;

    const columns = [
      "PoID",
      "PoNo",
      "PoDate",
      "CustomerName",
      "DestinationCountry",
      "CInvNo",
      "CInvDate",
      "PaymentTerms",
      "PaymentType",
      "CollectionStatus",
      "DueDate",
      "TransportedDate",
      "PaymentState",
      "UtilizedValue",
    ];

    const csv = [
      columns.map(csvEscape).join(","),
      ...sortedRows.map((row) =>
        columns.map((column) => csvEscape(row?.[column])).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `payments-received-pending-${status}.csv`;

    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const columns = [
    ["PoNo", "PO No"],
    ["PoDate", "PO Date"],
    ["CustomerName", "Customer / Consignee"],
    ["DestinationCountry", "Country"],
    ["CInvNo", "Invoice"],
    ["CInvDate", "Invoice Date"],
    ["PaymentTerms", "Payment Terms"],
    ["PaymentType", "Payment Type"],
    ["CollectionStatus", "ERP Payment Status"],
    ["DueDate", "Due Date"],
    ["TransportedDate", "Transported Date"],
    ["PaymentState", "CEO Payment State"],
    ["UtilizedValue", "Collected / Utilized"],
  ];

  return (
    <div className="erp-home-shell ceo-analytics-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content ceo-analytics-content">
        <div className="ceo-erp-content space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <button
                type="button"
                onClick={() => nav("/ceo-command-center?tab=commercial")}
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-3"
              >
                <ArrowLeft size={16} />
                Back to Commercial
              </button>

              <h1 className="text-2xl font-semibold text-slate-900">
                Payments Received / Pending
              </h1>

              <p className="text-sm text-slate-500 mt-1">
                ERP payment realization status by Customs Invoice Date.
                Received means the eligible PO has a realization record with
                RezStatus = 1. Pending means no such realized record exists.
              </p>
            </div>

            <button
              type="button"
              onClick={exportCsv}
              disabled={!sortedRows.length}
              className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium bg-white disabled:opacity-50"
              style={{ borderColor: "var(--c-border)" }}
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => applyStatus(option.value)}
                className={`px-4 py-2 rounded-xl border text-sm font-medium ${
                  status === option.value
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {[
              {
                label: "Eligible POs",
                value: summary?.total_count ?? 0,
                note: "Transported POs in selected CInvDate period",
              },
              {
                label: "Received",
                value: summary?.received_count ?? 0,
                note: `${summary?.received_pct ?? 0}% of eligible POs`,
              },
              {
                label: "Pending",
                value: summary?.pending_count ?? 0,
                note: `${summary?.pending_pct ?? 0}% of eligible POs`,
              },
              {
                label: "Realised Value",
                value: Number(summary?.realized_value ?? 0).toLocaleString(
                  undefined,
                  { maximumFractionDigits: 2 }
                ),
                note: "Collected / utilized value on received POs",
              },
            ].map((card) => (
              <div
                key={card.label}
                className="bg-white border rounded-2xl p-4"
                style={{ borderColor: "var(--c-border)" }}
              >
                <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">
                  {card.label}
                </div>
                <div className="text-3xl font-semibold text-slate-900 mt-2">
                  {card.value}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  {card.note}
                </div>
              </div>
            ))}
          </div>

          <div
            className="bg-white border rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"
            style={{ borderColor: "var(--c-border)" }}
          >
            <div className="text-sm text-slate-600">
              Date basis:{" "}
              <span className="font-medium text-slate-900">
                ScrmCustomInvoices.CInvDate
              </span>{" "}
              · {fromDate || "—"} → {toDate || "—"}
            </div>

            <div className="text-xs text-slate-500">
              Validation:{" "}
              <span className="font-semibold">
                {meta?.validation_state || "—"}
              </span>
            </div>
          </div>

          <div
            className="bg-white border rounded-2xl p-4"
            style={{ borderColor: "var(--c-border)" }}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="relative flex-1 min-w-[250px] max-w-xl">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search PO, customer, invoice, collection status..."
                  className="w-full border rounded-xl pl-9 pr-3 py-2 text-sm"
                />
              </div>

              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border rounded-xl px-3 py-2 text-sm bg-white"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} rows
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-sm text-slate-500">
              Loading payment realization details...
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          ) : (
            <div
              className="bg-white border rounded-2xl overflow-hidden"
              style={{ borderColor: "var(--c-border)" }}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      {columns.map(([field, label]) => (
                        <th
                          key={field}
                          onClick={() => changeSort(field)}
                          className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap cursor-pointer"
                        >
                          <span className="inline-flex items-center gap-1">
                            {label}
                            {sort.field === field &&
                              (sort.direction === "asc" ? (
                                <ChevronUp size={13} />
                              ) : (
                                <ChevronDown size={13} />
                              ))}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {pagedRows.length ? (
                      pagedRows.map((row, index) => (
                        <tr
                          key={`${row.PoID}-${index}`}
                          className="border-b last:border-b-0 hover:bg-slate-50"
                        >
                          {columns.map(([field]) => (
                            <td
                              key={field}
                              className="px-4 py-3 whitespace-nowrap text-slate-700"
                            >
                              {field === "PaymentState" ? (
                                <span
                                  className={`inline-flex px-2 py-1 rounded-full border text-xs font-semibold ${badgeClass(
                                    row?.[field]
                                  )}`}
                                >
                                  {row?.[field] || "—"}
                                </span>
                              ) : ["PoDate", "CInvDate", "DueDate", "TransportedDate"].includes(field) ? (
                                safeDate(row?.[field])
                              ) : field === "UtilizedValue" ? (
                                Number(row?.[field] ?? 0).toLocaleString(
                                  undefined,
                                  { maximumFractionDigits: 2 }
                                )
                              ) : (
                                row?.[field] ?? "—"
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="px-4 py-10 text-center text-slate-500"
                        >
                          No payment records found for this scope.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
                <div className="text-slate-500">
                  {sortedRows.length
                    ? `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(
                        currentPage * pageSize,
                        sortedRows.length
                      )} of ${sortedRows.length}`
                    : "0 records"}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="border rounded-lg px-3 py-1.5 disabled:opacity-40"
                  >
                    Previous
                  </button>

                  <span className="text-slate-600">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    className="border rounded-lg px-3 py-1.5 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl border p-4 text-sm ${
              meta?.reconciliation?.pass
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-red-200 bg-red-50 text-red-800"
            }`}
          >
            Reconciliation:{" "}
            <span className="font-semibold">
              {meta?.reconciliation?.equation || "—"}
            </span>
          </div>

          <div className="text-xs text-slate-500 leading-5 pb-6">
            Source lineage: eligible population from ScrmPoData,
            ScrmCustomInvoices and ScrmShipmentDetailsBreif. Received status
            from ScrmRealizationDetails.RezStatus = 1. Collected/utilized
            value from ScrmCollectionBreakUP.UtilizedValue. Pending monetary
            balance is intentionally not displayed because the ERP RemainingInvoiceValue
            depends on getPaymentValues/configuration logic and has not been
            independently reproduced in Analytics V2.
          </div>
        </div>
      </main>
    </div>
  );
}
